"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dialogue = void 0;
// @ts-ignore
var classnames_1 = require("classnames");
var PropTypes = require("prop-types");
var React = require("react");
var ariaHideOthers_1 = require("../../utils/ariaHideOthers");
var Focus = require("../../utils/focus");
var mutationObserver_1 = require("../../utils/mutationObserver");
var ButtonAction_1 = require("../ButtonAction");
var Icon_1 = require("../Icon");
var Backdrop_1 = require("./Backdrop");
var Facade_1 = require("./Facade");
var Dialogue = /** @class */ (function (_super) {
    __extends(Dialogue, _super);
    function Dialogue(props) {
        var _this = _super.call(this, props) || this;
        _this.modalMinHeight = 392;
        _this.modalMinWidth = 600;
        _this.modalRef = null;
        _this.modalContentRef = null;
        _this.transitionEndQueue = null;
        // Callback function to execute when mutations are observed
        _this.observerCallback = function (mutationsList, observer) {
            // Use traditional 'for loops' for IE 11
            for (var _i = 0, mutationsList_1 = mutationsList; _i < mutationsList_1.length; _i++) {
                var mutation = mutationsList_1[_i];
                if (mutation.type === 'childList') {
                    _this.adjustModalSize();
                }
                else if (mutation.type === 'attributes') {
                    _this.adjustModalSize();
                }
            }
        };
        /**
         * Debounces the resize event so it doesn't trigger too frequently
         */
        _this.resizeHandler = function (_, forceUpdate) {
            if (forceUpdate === void 0) { forceUpdate = false; }
            // When hidden only update when forced
            if (!_this.state.display && !forceUpdate) {
                return false;
            }
            if (_this.resizeTimeout) {
                clearTimeout(_this.resizeTimeout);
            }
            _this.resizeTimeout = setTimeout(function () {
                _this.adjustModalSize();
            }, 250);
        };
        /**
         * Update the size of the modal content container so that the
         * background Facade SVG can match the width and height.
         */
        _this.adjustModalSize = function () {
            if (_this.modalContentRef.current === null) {
                // Unable to reference modal
                return;
            }
            // Get size of modal
            var bounds = _this.modalContentRef.current.getBoundingClientRect();
            var height = bounds.height;
            height = Math.max(352, height);
            var width = bounds.width;
            width = Math.max(320, width);
            if (width === _this.state.modalWidth && height === _this.state.modalHeight) {
                // Size has not changed
                return;
            }
            // Size has changed
            _this.setState({
                modalHeight: height,
                modalWidth: width,
            });
        };
        /**
         * This method updates the state when a CSS transition has completed
         */
        _this.transitionEnd = function () {
            if (_this.transitionEndQueue) {
                _this.setState(_this.transitionEndQueue);
                _this.transitionEndQueue = null;
            }
        };
        /**
         * When user clicks on backdrop/overlay
         */
        _this.clickBackdrop = function (event) {
            if (event.target === event.currentTarget && !!_this.props.closeOnClick) {
                _this.props.closeModal(event);
            }
        };
        /**
         * When tabbing backwards, prevent focus from leaving the modal
         */
        _this.focusTrapFirstElement = function (event) {
            if (event.keyCode === 9 && event.shiftKey) {
                event.preventDefault();
            }
        };
        /**
         * When an element receives focus, set the focus back to the modal
         */
        _this.focusTrapOnFocus = function (_) {
            _this.modalRef.current.focus();
        };
        /**
         * The keydown handler is used for detecting escape press, tabbing,
         * and shift-tabbing (reverse tab). The alternative keyPress handler
         * wasn't useful at reverse tabbing.
         */
        _this.keyDownHandler = function (event) {
            // If modal is open and escape was pressed, close the modal
            if (_this.props.isOpen && event.keyCode === 27 && !!_this.props.closeOnEsc) {
                _this.props.closeModal(event);
            }
            if (event.target === event.currentTarget) {
                // This ignores keypress on other elements like buttons
                _this.focusTrapFirstElement(event);
            }
        };
        _this.modalRef = React.createRef();
        _this.modalContentRef = React.createRef();
        _this.state = {
            display: _this.props.isOpen,
            modalHeight: _this.modalMinHeight,
            modalWidth: _this.modalMinWidth,
            show: _this.props.isOpen,
        };
        return _this;
    }
    Dialogue.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.resizeHandler);
        document.body.classList.remove('modal-open');
        if (this.observer) {
            this.observer.disconnect();
        }
    };
    Dialogue.prototype.componentDidMount = function () {
        window.addEventListener('resize', this.resizeHandler);
        this.resizeHandler(null, true);
        // Setup a mutation observer to resize the Dialogue when the content changes
        var config = {
            attributes: true,
            childList: true,
            subtree: true,
        };
        // Create an observer instance linked to the callback function
        // @ts-ignore
        this.observer = new mutationObserver_1.mutationObserver(this.observerCallback);
        // Start observing the target node for configured mutations
        this.observer.observe(this.modalContentRef.current, config);
        if (this.state.display) {
            document.body.classList.add('modal-open');
            this.modalRef.current.focus();
            if (!!this.props.ariaHide) {
                (0, ariaHideOthers_1.ariaHideOthers)(this.modalRef.current);
            }
        }
    };
    Dialogue.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (!prevProps.isOpen && this.props.isOpen) {
            // When modal is opened, save a reference to the element which opened
            // the dialog so focus can be restored when the modal is closed. This
            // will default to document.activeElement however in some browsers
            // clicking on a button won't give it focus so a reference to the
            // button might need to be passed in.
            Focus.saveFocus(this.props.restoreFocusTo);
            // Prevent page from scrolling
            document.body.classList.add('modal-open');
            // Set aria-hidden on sibling elements up to body.
            if (!!this.props.ariaHide) {
                (0, ariaHideOthers_1.ariaHideOthers)(this.modalRef.current);
            }
            if (!!this.props.fade) {
                // Open with fade
                this.setState({
                    display: true,
                });
            }
            else {
                // Open without fade
                this.setState({
                    display: true,
                    show: true,
                });
            }
        }
        if (!prevState.display && this.state.display) {
            this.modalRef.current.focus();
            if (this.state.show === false) {
                this.setState({
                    show: true,
                });
            }
        }
        if (prevProps.isOpen && !this.props.isOpen) {
            // When closed, return focus to the element that triggered opening
            Focus.restoreFocus();
            // Allow page to scroll
            document.body.classList.remove('modal-open');
            // Remove aria-hidden on sibling elements up to body.
            if (!!this.props.ariaHide) {
                (0, ariaHideOthers_1.ariaRevertOthers)(this.modalRef.current);
            }
            if (!!this.props.fade) {
                // Close with fade
                this.setState({
                    show: false,
                });
                this.transitionEndQueue = {
                    display: false,
                };
            }
            else {
                // Close without fade
                this.setState({
                    display: false,
                    show: false,
                });
            }
        }
        // Check modal size if any of the content has changed
        var contentUpdated = (prevProps.title !== this.props.title) ||
            (prevProps.message !== this.props.message) ||
            (prevProps.children !== this.props.children) ||
            (!prevState.display && this.state.display);
        if (contentUpdated) {
            this.adjustModalSize();
        }
    };
    Dialogue.prototype.render = function () {
        var classes = (0, classnames_1.default)('modal', {
            'd-block': this.state.display,
            'd-none': !this.state.display,
            'fade': !!this.props.fade,
            'show': this.state.show,
        });
        var attributes = {
            'aria-hidden': this.props.isOpen ? false : true,
            'aria-labelledby': this.props.id + "-label",
            className: classes,
            id: this.props.id,
            onClick: this.clickBackdrop,
            onKeyDown: this.keyDownHandler,
            onTransitionEnd: this.transitionEnd,
            ref: this.modalRef,
            role: 'dialog',
            tabIndex: 0,
        };
        if (!!this.props.ariaModal) {
            attributes['aria-modal'] = true;
        }
        var defaultIcon = {
            element: (React.createElement(Icon_1.Icon, { symbol: "messageSuccess64" })),
            height: 64,
            width: 64,
        };
        return (React.createElement(React.Fragment, null,
            React.createElement("div", __assign({}, attributes),
                React.createElement("div", { className: "modal-dialog", role: "document" },
                    React.createElement(Facade_1.Facade, { width: this.state.modalWidth, height: this.state.modalHeight, icon: this.props.icon ? this.props.icon : defaultIcon }),
                    React.createElement("div", { className: "modal-content", ref: this.modalContentRef },
                        React.createElement("div", { className: "modal-header" },
                            React.createElement("h1", { className: "modal-title", id: this.props.id + "-label" }, this.props.title)),
                        React.createElement("div", { className: "modal-body" },
                            this.props.message && (React.createElement("p", null, this.props.message)),
                            !this.props.message && this.props.children),
                        React.createElement("div", { className: "modal-footer" },
                            this.props.buttonEmphasis && (React.createElement(ButtonAction_1.ButtonActionEmphasis, __assign({}, this.props.buttonEmphasis))),
                            this.props.buttonDefault && (React.createElement(ButtonAction_1.ButtonActionDefault, __assign({}, this.props.buttonDefault))),
                            this.props.buttonSubtle && !(this.props.buttonEmphasis && this.props.buttonDefault) && (React.createElement(ButtonAction_1.ButtonActionSubtle, __assign({}, this.props.buttonSubtle)))),
                        React.createElement("span", { tabIndex: 0, className: "modal-focustrap", onFocus: this.focusTrapOnFocus, "aria-hidden": "true" }, "\u00A0")))),
            React.createElement(Backdrop_1.Backdrop, { isOpen: this.props.isOpen, fade: !!this.props.fade })));
    };
    return Dialogue;
}(React.Component));
exports.Dialogue = Dialogue;
/* tslint:disable:no-string-literal */
Dialogue['defaultProps'] = {
    ariaHide: true,
    ariaModal: false,
    closeOnClick: true,
    closeOnEsc: true,
    fade: false,
    isOpen: false,
};
Dialogue['propTypes'] = {
    ariaHide: PropTypes.bool,
    ariaModal: PropTypes.bool,
    buttonDefault: PropTypes.shape({ children: PropTypes.string.isRequired, id: PropTypes.string, onClick: PropTypes.func.isRequired }),
    buttonEmphasis: PropTypes.shape({ children: PropTypes.string.isRequired, id: PropTypes.string, onClick: PropTypes.func.isRequired }),
    buttonSubtle: PropTypes.shape({ children: PropTypes.string.isRequired, id: PropTypes.string, onClick: PropTypes.func.isRequired }),
    children: PropTypes.element,
    closeModal: PropTypes.func.isRequired,
    closeOnClick: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    fade: PropTypes.bool,
    icon: PropTypes.shape({ element: PropTypes.element.isRequired, height: PropTypes.number.isRequired, width: PropTypes.number.isRequired }),
    id: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    message: PropTypes.string,
    restoreFocusTo: PropTypes.element,
    title: PropTypes.string.isRequired,
    validateMessage: function (props) {
        if (props.message && props.children) {
            return new Error('Pass either "message" or "children" but not both.');
        }
        return null;
    },
    validateButtons: function (props) {
        if (props.buttonEmphasis && props.buttonDefault && props.buttonSubtle) {
            return new Error('The dialogue should not have more than two buttons.');
        }
        return null;
    },
};
/* tslint:enable:no-string-literal */
